import { type PropsWithChildren, useState } from "react"
import { useSafeContext } from "@oefr/hooks"
import { MarketName } from "@/constants/marketName"
import { type InputMethod } from "@/features/subscription/meterEligibility/types/inputMethod"
import { type SelectedEnergyTypes } from "@/features/subscription/types/selectedEnergyTypes"
import { JourneyType } from "@/scalars/journeyType"
import { createNamedContext } from "@/utils/create-named-context"
import { type OnboardingState } from "../../wizard"

type MeterPoint = {
    meterPointId: string
    marketName: MarketName
    familyName: string
    givenName: string
    hasChangeOfSupplierError?: boolean
}

function transformStateToMeterPoints(formValues: OnboardingState["formValues"]): MeterPoint[] {
    const meterPoints: MeterPoint[] = []

    for (const energyType of formValues.selectedEnergyTypes) {
        meterPoints.push({
            meterPointId: formValues[energyType].supplyPoint.meterPoint.id,
            marketName: formValues[energyType].marketName,
            familyName: formValues.familyName,
            givenName: formValues.givenName,
        })
    }

    return meterPoints
}

type InitialOnboardingFormContextValue = {
    selectedEnergyTypes: SelectedEnergyTypes
    familyName: string
    getElectricityMeterPoint: () => MeterPoint | undefined
    getGasMeterPoint: () => MeterPoint | undefined
    givenName: string
    inputMethod: InputMethod
    journeyType: JourneyType
    onboardingState: OnboardingState
    setSelectedEnergyTypes: React.Dispatch<React.SetStateAction<SelectedEnergyTypes>>
    setInputMethod: React.Dispatch<React.SetStateAction<InputMethod>>
    setMeterPoints: React.Dispatch<React.SetStateAction<MeterPoint[]>>
    updateJourneyType: (newJourneyType: JourneyType) => void
}

const InitialOnboardingForm =
    createNamedContext<InitialOnboardingFormContextValue>("InitialOnboardingForm")

type InitialOnboardingFormProviderProps = PropsWithChildren<{
    onboardingState: OnboardingState
}>

export function InitialOnboardingFormProvider({
    children,
    onboardingState,
}: InitialOnboardingFormProviderProps) {
    const [selectedEnergyTypes, setSelectedEnergyTypes] = useState(
        onboardingState.formValues.selectedEnergyTypes
    )
    const [inputMethod, setInputMethod] = useState(onboardingState.formValues.inputMethod)
    const [journeyType, setJourneyType] = useState(onboardingState.formValues.journeyType)
    const [familyName] = useState(onboardingState.formValues.familyName)
    const [givenName] = useState(onboardingState.formValues.givenName)
    const [meterPoints, setMeterPoints] = useState(
        transformStateToMeterPoints(onboardingState.formValues)
    )

    const updateJourneyType = (newJourneyType: JourneyType) => {
        setJourneyType(newJourneyType)

        if (newJourneyType === JourneyType.COMMISSIONING) {
            setInputMethod("meterPoint")
        }
    }

    const getElectricityMeterPoint = () => {
        return meterPoints.find((mp) => mp.marketName === MarketName.FRA_ELECTRICITY)
    }

    const getGasMeterPoint = () => {
        return meterPoints.find((mp) => mp.marketName === MarketName.FRA_GAS)
    }

    return (
        <InitialOnboardingForm.Provider
            value={{
                selectedEnergyTypes,
                familyName,
                getElectricityMeterPoint,
                getGasMeterPoint,
                givenName,
                inputMethod,
                journeyType,
                onboardingState,
                setSelectedEnergyTypes,
                setInputMethod,
                setMeterPoints,
                updateJourneyType,
            }}
        >
            {children}
        </InitialOnboardingForm.Provider>
    )
}

export function useInitialOnboardingFormContext() {
    return useSafeContext(InitialOnboardingForm)
}
